import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  MedidaName,
  OrderedMedidas,
  SelectorState,
  TEMPORADA,
  Temporada,
} from '../../../context/reducers/selectorReducer'
import { selectEstacion } from '../../../context/actions/selectorActions'
import { IAppState } from '../../../context/context'
import {
  selectAllMedidas,
  selectSelector,
  selectSkipInfo,
} from '../../../context/selectors/selectorSelectors'
import { resolveEffectiveEstacion, type EstacionSyncValue } from './estacionSyncUtils'

export { resolveEffectiveEstacion } from './estacionSyncUtils'

export function dispatchCatalogEstacion(
  dispatch: (action: unknown) => void,
  estacion: EstacionSyncValue | Temporada | null,
  fromLockedLanding: boolean
) {
  if (estacion == null) return
  if (fromLockedLanding) {
    dispatch(setEstacionSeleccionadaPorUsuario(estacion as Temporada))
  } else {
    dispatch(setEstacionSeleccionada(estacion as Temporada))
  }
}
import {
  fetchMedidaOpciones,
  refreshMedidasOpciones,
  setMedidasConfirmed,
  setMedidasIniciales,
  setOpcionesMedidas,
  setPendingAutoFocusNextMedida,
  setEstacionSeleccionada,
  setEstacionSeleccionadaPorUsuario,
  setVehicleTyreSelectionWasPartial,
  updateMedidaSeleccionada,
} from '../../../../coche/Neumaticos/Buscador/SeleccionMedidas/Infrastructure/store/medidasSlice'
import {
  selectHasAllMedidas,
  selectMedidasData,
  selectPendingAutoFocusNextMedida,
  selectSelectedEstacion,
  selectEstacionSeleccionadaPorUsuario,
  selectSelectedMedidas,
} from '../../../../coche/Neumaticos/Buscador/SeleccionMedidas/Infrastructure/store/medidasSelectors'
import {
  catalogHasAnyMedidaSelection,
  findNextMedidaToComplete,
  hasCompleteMedidasSelection,
  isMedidaUnlocked,
  selectedMedidasArePartial,
  stripInvalidDownstreamMedidas,
} from '../../../../coche/Neumaticos/Buscador/SeleccionMedidas/Infrastructure/store/medidasPorMatricula.utils'

export function isLegacyTemporadaLocked(state: IAppState): boolean {
  return selectSkipInfo(state).temporada
}

type UseSyncedMedidasSelectorFieldsOptions = {
  autoOpenDropdown?: boolean
  onAutoFocusMedida?: (name: MedidaName | null) => void
  /** Selector de la home: defaults visuals + desbloqueig seqüencial sense flux AMI. */
  isHomeSelector?: boolean
}

type MedidasSeleccionadas = { [k in MedidaName]?: string | number | null }

/** Defaults visuals de la home (205/55/R16/91/V) — alineats amb SelectorHome. */
export const HOME_SELECTOR_PLACEHOLDER_MEDIDAS: Record<
  MedidaName,
  string | number
> = {
  ancho: 205,
  serie: 55,
  llanta: 16,
  carga: 91,
  velocidad: 'V',
}

const medidaHasValue = (value: string | number | null | undefined): boolean =>
  value !== null && value !== undefined && value !== ''

function resolveHomeMedidaValue(
  field:
    | { value?: string | number | null; default?: string | number | null }
    | undefined,
  name: MedidaName,
  useHomePlaceholders: boolean
): string | number | null {
  const v = field?.value ?? field?.default
  if (medidaHasValue(v)) return v
  if (useHomePlaceholders) return HOME_SELECTOR_PLACEHOLDER_MEDIDAS[name]
  return null
}

function resolveHomeGuidedDisplayValue(
  name: MedidaName,
  selectedMedidas: MedidasSeleccionadas,
  medidasForDisplay: MedidasSeleccionadas
): string | number {
  if (medidaHasValue(selectedMedidas[name])) {
    return selectedMedidas[name] as string | number
  }
  const display = medidasForDisplay[name]
  if (medidaHasValue(display)) return display as string | number
  return HOME_SELECTOR_PLACEHOLDER_MEDIDAS[name]
}

function valueIsInOptions(
  value: string | number | null | undefined,
  options: Array<{ label: string; value: string | number | null }>
): boolean {
  if (!medidaHasValue(value)) return false
  return options.some((o) => String(o.value) === String(value))
}

function withValueInOptions(
  value: string | number,
  options: Array<{ label: string; value: string | number | null }>
): Array<{ label: string; value: string | number | null }> {
  if (options.some((o) => String(o.value) === String(value))) {
    return options
  }
  return [{ label: String(value), value }, ...options]
}

function resolveHomeGuidedFieldState(
  name: MedidaName,
  selectedMedidas: MedidasSeleccionadas,
  medidasForDisplay: MedidasSeleccionadas,
  catalogOptions: Array<{ label: string; value: string | number | null }>,
  legacyOptions: Array<{ label: string; value: string | number | null }>,
  loading: boolean,
  useDefaultPlaceholdersInDropdown: boolean
): {
  value: string | number | null
  options: Array<{ label: string; value: string | number | null }>
} {
  const useCatalogOptions = loading || catalogOptions.length > 0
  const options = useCatalogOptions ? catalogOptions : legacyOptions

  if (useCatalogOptions && loading) {
    return { value: null, options }
  }

  if (medidaHasValue(selectedMedidas[name])) {
    const selected = selectedMedidas[name] as string | number
    return {
      value: valueIsInOptions(selected, options) ? selected : null,
      options,
    }
  }

  if (!useDefaultPlaceholdersInDropdown) {
    return { value: null, options }
  }

  const placeholder = resolveHomeGuidedDisplayValue(
    name,
    selectedMedidas,
    medidasForDisplay
  )

  if (valueIsInOptions(placeholder, options)) {
    return { value: placeholder, options }
  }

  if (useCatalogOptions) {
    return { value: null, options }
  }

  return {
    value: placeholder,
    options: withValueInOptions(placeholder, options),
  }
}

function resolvePriorMedidaConfirmValue(
  prior: MedidaName,
  snapshot: MedidasSeleccionadas,
  medidasForDisplay: MedidasSeleccionadas,
  catalogOptions: Array<{ label: string; value: string | number | null }>,
  legacyOptions: Array<{ label: string; value: string | number | null }>,
  loading: boolean
): string | number | null {
  if (medidaHasValue(snapshot[prior])) {
    const selected = snapshot[prior] as string | number
    const options = loading || catalogOptions.length > 0 ? catalogOptions : legacyOptions
    return valueIsInOptions(selected, options) ? selected : null
  }

  const placeholder = resolveHomeGuidedDisplayValue(
    prior,
    snapshot,
    medidasForDisplay
  )
  return medidaHasValue(placeholder) ? placeholder : null
}

function buildHomeEffectiveSnapshot(
  selectedMedidas: MedidasSeleccionadas,
  medidasForDisplay: MedidasSeleccionadas
): MedidasSeleccionadas {
  const snapshot: MedidasSeleccionadas = {}
  for (const name of OrderedMedidas) {
    snapshot[name] = medidaHasValue(selectedMedidas[name])
      ? selectedMedidas[name]
      : resolveHomeGuidedDisplayValue(name, selectedMedidas, medidasForDisplay)
  }
  return snapshot
}

function mergeMedidasWithLegacyDefaults(
  selectedMedidas: MedidasSeleccionadas,
  legacyMedidas: ReturnType<typeof selectAllMedidas> | undefined
): MedidasSeleccionadas {
  const merged: MedidasSeleccionadas = { ...selectedMedidas }

  for (const name of OrderedMedidas) {
    const selected = merged[name]
    if (selected !== null && selected !== undefined && selected !== '') continue

    const legacy = legacyMedidas?.[name]
    const legacyValue = legacy?.value ?? legacy?.default ?? null
    if (legacyValue !== null && legacyValue !== undefined && legacyValue !== '') {
      merged[name] = legacyValue
    }
  }

  return merged
}

export function useSyncedMedidasSelectorFields(
  options: UseSyncedMedidasSelectorFieldsOptions = {}
) {
  const {
    autoOpenDropdown = true,
    onAutoFocusMedida,
    isHomeSelector = false,
  } = options
  const dispatch = useDispatch()
  const medidasData = useSelector(selectMedidasData)
  const selectedMedidas = useSelector(selectSelectedMedidas)
  const pendingAutoFocusNextMedida = useSelector(selectPendingAutoFocusNextMedida)
  const legacyMedidas = useSelector(selectAllMedidas)
  const hasAllMedidas = useSelector(selectHasAllMedidas)

  const hasCompleteHomeMedidas = hasCompleteMedidasSelection(selectedMedidas)
  const isFreshHomeSearch =
    !hasCompleteHomeMedidas && !selectedMedidasArePartial(selectedMedidas)
  const isHomeGuidedFlow = isHomeSelector && !hasCompleteHomeMedidas

  const medidasForDisplay = useMemo(() => {
    if (hasCompleteHomeMedidas) {
      return selectedMedidas
    }
    return mergeMedidasWithLegacyDefaults(selectedMedidas, legacyMedidas)
  }, [hasCompleteHomeMedidas, legacyMedidas, selectedMedidas])

  const selectRefs = useRef<Partial<Record<MedidaName, HTMLSelectElement | null>>>(
    {}
  )
  const lastAutoOpenedFor = useRef<MedidaName | null>(null)
  const lastRefreshedCoreKey = useRef('')
  const lastChangedMedidaRef = useRef<MedidaName | null>(null)
  const homeRestoreSnapshotRef = useRef<MedidasSeleccionadas | null>(null)
  const autoOpenPendingRef = useRef<MedidaName | null>(null)
  const [homeEditInProgress, setHomeEditInProgress] = useState(false)

  // Context de catàleg/home actiu quan hi ha mides completes (tornada des de resultats)
  // o selecció parcial (usuari o vehicle triant camp a camp). Fresh home (cap valor a
  // Redux) queda fora i continua usant defaults legacy.
  const hasCatalogMedidasContext =
    hasCompleteHomeMedidas || selectedMedidasArePartial(selectedMedidas)

  const isPartialVehicleMedidasSelection = selectedMedidasArePartial(
    selectedMedidas
  )

  // El desbloqueig seqüencial (ancho → serie → …) només ha de tenir en compte
  // valors triats a Redux; els placeholders visuals de la home no compten.
  const medidasForUnlock = selectedMedidas

  useEffect(() => {
    if (isHomeGuidedFlow) return
    if (!hasCatalogMedidasContext || !isPartialVehicleMedidasSelection) return

    const patch: Partial<
      Record<
        MedidaName,
        {
          value: string | number | null
          options?: unknown[]
          loading?: boolean
        }
      >
    > = {}
    let changed = false

    for (const name of OrderedMedidas) {
      const selected = selectedMedidas[name]
      const hasSelected =
        selected !== null && selected !== undefined && selected !== ''
      const current = medidasData[name]

      if (!hasSelected && current?.value != null && current.value !== '') {
        patch[name] = {
          ...current,
          value: null,
        }
        changed = true
      }
    }

    if (changed) {
      dispatch(setOpcionesMedidas(patch))
    }
  }, [
    dispatch,
    hasCatalogMedidasContext,
    isHomeGuidedFlow,
    isPartialVehicleMedidasSelection,
    medidasData,
    selectedMedidas,
  ])

  useEffect(() => {
    const sanitized = stripInvalidDownstreamMedidas(selectedMedidas)
    if (sanitized) {
      if (!isHomeGuidedFlow) {
        dispatch(setMedidasIniciales(sanitized))
      }
      return
    }

    if (
      isHomeGuidedFlow ||
      !hasCatalogMedidasContext ||
      !selectedMedidasArePartial(selectedMedidas)
    ) {
      return
    }

    const coreKey = `${selectedMedidas.ancho ?? ''}/${selectedMedidas.serie ?? ''}/${selectedMedidas.llanta ?? ''}`
    if (coreKey === lastRefreshedCoreKey.current) {
      return
    }

    lastRefreshedCoreKey.current = coreKey
    dispatch(refreshMedidasOpciones())
  }, [dispatch, hasCatalogMedidasContext, isHomeGuidedFlow, selectedMedidas])

  const abortHomeMedidasEdit = useCallback(() => {
    const snapshot = homeRestoreSnapshotRef.current
    if (!isHomeGuidedFlow || !homeEditInProgress || !snapshot) return

    homeRestoreSnapshotRef.current = null
    lastChangedMedidaRef.current = null
    lastAutoOpenedFor.current = null
    autoOpenPendingRef.current = null
    setHomeEditInProgress(false)

    dispatch(setMedidasIniciales(snapshot))
    dispatch(setPendingAutoFocusNextMedida(false))
    dispatch(setMedidasConfirmed(hasCompleteMedidasSelection(snapshot)))
    dispatch(refreshMedidasOpciones())

    onAutoFocusMedida?.(null)
  }, [dispatch, homeEditInProgress, isHomeGuidedFlow, onAutoFocusMedida])

  const beginHomeMedidasEditIfNeeded = useCallback(() => {
    if (!isHomeGuidedFlow || homeEditInProgress) return

    homeRestoreSnapshotRef.current = buildHomeEffectiveSnapshot(
      selectedMedidas,
      medidasForDisplay
    )
    setHomeEditInProgress(true)
    dispatch(setMedidasConfirmed(false))
  }, [dispatch, homeEditInProgress, isHomeGuidedFlow, medidasForDisplay, selectedMedidas])

  useEffect(() => {
    if (!autoOpenDropdown) return

    if (isHomeGuidedFlow) {
      const changed = lastChangedMedidaRef.current
      if (!changed) return

      const index = OrderedMedidas.indexOf(changed)
      const next = OrderedMedidas[index + 1]
      if (!next) {
        lastChangedMedidaRef.current = null
        onAutoFocusMedida?.(null)
        return
      }

      if (medidaHasValue(selectedMedidas[next])) {
        lastChangedMedidaRef.current = null
        return
      }

      const field = medidasData[next]
      if (!field || field.loading) return

      const fieldOptions = field.options ?? []
      if (fieldOptions.length === 0) return

      if (fieldOptions.length === 1) {
        dispatch(
          updateMedidaSeleccionada({ name: next, value: fieldOptions[0].value })
        )
        lastChangedMedidaRef.current = next
        lastAutoOpenedFor.current = null
        return
      }

      if (lastAutoOpenedFor.current === next) return

      const el = selectRefs.current[next]
      if (!el) return

      lastAutoOpenedFor.current = next
      autoOpenPendingRef.current = next
      lastChangedMedidaRef.current = null
      el.focus()
      try {
        el.showPicker?.()
      } catch {
        // showPicker no disponible en aquest navegador
      }
      window.setTimeout(() => {
        if (autoOpenPendingRef.current === next) {
          autoOpenPendingRef.current = null
        }
      }, 150)
      onAutoFocusMedida?.(next)
      return
    }

    if (!hasCompleteHomeMedidas && !selectedMedidasArePartial(selectedMedidas)) {
      lastAutoOpenedFor.current = null
      onAutoFocusMedida?.(null)
      return
    }
    if (!selectedMedidasArePartial(selectedMedidas)) {
      lastAutoOpenedFor.current = null
      onAutoFocusMedida?.(null)
      return
    }

    const next = findNextMedidaToComplete(selectedMedidas)
    if (!next) {
      onAutoFocusMedida?.(null)
      return
    }

    const selectedValue = selectedMedidas[next]
    if (selectedValue != null && selectedValue !== '') return

    const field = medidasData[next]
    if (!field || field.loading) return

    const fieldOptions = field.options ?? []
    if (fieldOptions.length === 0) return

    if (fieldOptions.length === 1) {
      dispatch(
        updateMedidaSeleccionada({ name: next, value: fieldOptions[0].value })
      )
      lastAutoOpenedFor.current = null
      return
    }

    if (lastAutoOpenedFor.current === next && !pendingAutoFocusNextMedida) {
      return
    }

    const el = selectRefs.current[next]
    if (!el) return

    lastAutoOpenedFor.current = next
    el.focus()
    try {
      el.showPicker?.()
    } catch {
      // showPicker no disponible en aquest navegador
    }
    onAutoFocusMedida?.(next)
    if (pendingAutoFocusNextMedida) {
      dispatch(setPendingAutoFocusNextMedida(false))
    }
  }, [
    autoOpenDropdown,
    dispatch,
    hasCompleteHomeMedidas,
    isHomeGuidedFlow,
    medidasData,
    onAutoFocusMedida,
    pendingAutoFocusNextMedida,
    selectedMedidas,
  ])

  const handleHomeMedidaBlur = useCallback(
    (name: MedidaName) => {
      if (!isHomeGuidedFlow || !homeEditInProgress) return

      window.setTimeout(() => {
        if (autoOpenPendingRef.current === name) return

        if (lastAutoOpenedFor.current !== name) return
        if (medidaHasValue(selectedMedidas[name])) {
          lastAutoOpenedFor.current = null
          return
        }

        const active = document.activeElement
        if (
          active instanceof HTMLSelectElement &&
          OrderedMedidas.includes(active.name as MedidaName)
        ) {
          return
        }

        abortHomeMedidasEdit()
      }, 0)
    },
    [abortHomeMedidasEdit, homeEditInProgress, isHomeGuidedFlow, selectedMedidas]
  )

  const getEffectiveField = useCallback(
    (name: MedidaName) => {
      const current = medidasData[name]
      const legacy = legacyMedidas?.[name]

      if (!current) return legacy
      if (!legacy) return current

      const unlocked = isMedidaUnlocked(name, medidasForUnlock)
      const selected = medidasForDisplay[name]
      const hasSelectedInRedux = medidaHasValue(selected)

      if (isHomeGuidedFlow) {
        const useDefaultPlaceholdersInDropdown =
          isFreshHomeSearch && !homeEditInProgress
        const { value, options } = resolveHomeGuidedFieldState(
          name,
          selectedMedidas,
          medidasForDisplay,
          current.options ?? [],
          legacy.options ?? [],
          Boolean(current.loading),
          useDefaultPlaceholdersInDropdown
        )

        return {
          ...current,
          value,
          options,
          loading: current.loading ?? false,
        }
      }

      if (hasCatalogMedidasContext && isPartialVehicleMedidasSelection) {
        if (!unlocked) {
          return {
            ...current,
            value: null,
            options: [],
            loading: false,
          }
        }

        const selectedInRedux = selectedMedidas[name]
        const hasSelectedInRedux =
          selectedInRedux !== null &&
          selectedInRedux !== undefined &&
          selectedInRedux !== ''

        if (!hasSelectedInRedux) {
          return {
            ...current,
            value: null,
            options: current?.options ?? [],
            loading: current?.loading ?? false,
          }
        }

        const options = current.options ?? []
        const inList = options.some(
          (o) => String(o.value) === String(selectedInRedux)
        )
        return {
          ...current,
          value: inList ? selectedInRedux : null,
          options,
          loading: false,
        }
      }

      const hasCurrentValue =
        unlocked && current.value !== null && current.value !== undefined
      const hasCurrentOptions =
        unlocked &&
        Array.isArray(current.options) &&
        current.options.length > 0

      if (hasCurrentValue || (hasCurrentOptions && hasCatalogMedidasContext)) {
        return current
      }

      if (!unlocked) {
        return {
          ...current,
          value: null,
          options: [],
          loading: false,
        }
      }

      if (hasCatalogMedidasContext) {
        const hasSelected =
          unlocked &&
          selected !== null &&
          selected !== undefined &&
          selected !== ''

        if (hasSelected) {
          const options = current.options ?? []
          const inList = options.some(
            (o) => String(o.value) === String(selected)
          )
          return {
            ...current,
            value: selected,
            options: inList
              ? options
              : [{ label: String(selected), value: selected }, ...options],
            loading: false,
          }
        }

        return current
      }

      const resolvedValue =
        medidasForDisplay[name] ??
        legacy.value ??
        legacy.default ??
        (isFreshHomeSearch ? HOME_SELECTOR_PLACEHOLDER_MEDIDAS[name] : null)

      return {
        ...current,
        value: resolvedValue,
        options: legacy.options ?? [],
        loading: legacy.loading ?? current.loading,
      }
    },
    [hasCatalogMedidasContext, homeEditInProgress, isFreshHomeSearch, isHomeGuidedFlow, isPartialVehicleMedidasSelection, legacyMedidas, medidasData, medidasForDisplay, medidasForUnlock, selectedMedidas]
  )

  const fields = {
    ancho: getEffectiveField('ancho'),
    serie: getEffectiveField('serie'),
    llanta: getEffectiveField('llanta'),
    carga: getEffectiveField('carga'),
    velocidad: getEffectiveField('velocidad'),
  }

  const hasAllEffectiveMedidas = OrderedMedidas.every((name) => {
    if (isHomeGuidedFlow && (homeEditInProgress || selectedMedidasArePartial(selectedMedidas))) {
      return medidaHasValue(selectedMedidas[name])
    }

    const resolved = resolveHomeMedidaValue(
      fields[name],
      name,
      isHomeGuidedFlow || isFreshHomeSearch
    )
    return medidaHasValue(resolved)
  })

  const isFieldDisabled = useCallback(
    (name: MedidaName) => {
      if (isHomeGuidedFlow) return false
      return !isMedidaUnlocked(name, medidasForUnlock)
    },
    [isHomeGuidedFlow, medidasForUnlock]
  )

  const confirmPriorHomeMedidasForField = useCallback(
    (name: MedidaName) => {
      if (!isHomeGuidedFlow) return

      const index = OrderedMedidas.indexOf(name)
      if (index <= 0) return

      for (let i = 0; i < index; i++) {
        const prior = OrderedMedidas[i]
        if (medidaHasValue(selectedMedidas[prior])) continue

        const priorValue = resolvePriorMedidaConfirmValue(
          prior,
          selectedMedidas,
          medidasForDisplay,
          medidasData[prior]?.options ?? [],
          legacyMedidas?.[prior]?.options ?? [],
          Boolean(medidasData[prior]?.loading)
        )
        if (!medidaHasValue(priorValue)) continue

        dispatch(
          updateMedidaSeleccionada({
            name: prior,
            value: priorValue,
          })
        )
      }
    },
    [dispatch, isHomeGuidedFlow, legacyMedidas, medidasData, medidasForDisplay, selectedMedidas]
  )

  const ensureFieldOptions = useCallback(
    (name: MedidaName) => {
      if (isHomeGuidedFlow) {
        confirmPriorHomeMedidasForField(name)
      } else if (!isMedidaUnlocked(name, medidasForUnlock)) {
        return
      }

      const current = medidasData[name]
      const hasOptions =
        Array.isArray(current?.options) && current.options.length > 0
      if (!hasOptions && (isHomeGuidedFlow || selectedMedidasArePartial(selectedMedidas))) {
        dispatch(fetchMedidaOpciones(name))
      }
    },
    [
      confirmPriorHomeMedidasForField,
      dispatch,
      isHomeGuidedFlow,
      medidasData,
      medidasForUnlock,
      selectedMedidas,
    ]
  )

  const registerSelectRef = useCallback(
    (name: MedidaName, el: HTMLSelectElement | null) => {
      selectRefs.current[name] = el
    },
    []
  )

  const hydrateCatalogMedidas = useCallback(() => {
    const opcionesPayload: Partial<
      Record<MedidaName, { value: string | number | null; options: unknown[] }>
    > = {}
    const inicialesPayload: { [k in MedidaName]?: string | number | null } = {}

    for (const name of OrderedMedidas) {
      const field = getEffectiveField(name)
      const resolvedValue = resolveHomeMedidaValue(
        field,
        name,
        isHomeGuidedFlow || isFreshHomeSearch
      )
      opcionesPayload[name] = {
        ...(field || {}),
        value: resolvedValue,
        options: field?.options ?? [],
      }
      inicialesPayload[name] = resolvedValue
    }

    dispatch(setOpcionesMedidas(opcionesPayload))
    dispatch(setMedidasIniciales(inicialesPayload))
    dispatch(setVehicleTyreSelectionWasPartial(false))
  }, [dispatch, getEffectiveField, isFreshHomeSearch, isHomeGuidedFlow])

  const handleMedidaChange = useCallback(
    (name: MedidaName, value: string | number | null) => {
      if (isHomeGuidedFlow) {
        beginHomeMedidasEditIfNeeded()
      }

      let baseSelected: MedidasSeleccionadas = { ...selectedMedidas }

      if (isHomeGuidedFlow) {
        const index = OrderedMedidas.indexOf(name)
        for (let i = 0; i < index; i++) {
          const prior = OrderedMedidas[i]
          if (medidaHasValue(baseSelected[prior])) continue

          const priorValue = resolvePriorMedidaConfirmValue(
            prior,
            baseSelected,
            medidasForDisplay,
            medidasData[prior]?.options ?? [],
            legacyMedidas?.[prior]?.options ?? [],
            Boolean(medidasData[prior]?.loading)
          )
          if (!medidaHasValue(priorValue)) continue

          baseSelected[prior] = priorValue
          dispatch(
            updateMedidaSeleccionada({
              name: prior,
              value: priorValue,
            })
          )
        }
      }

      const nextSelected: MedidasSeleccionadas = {
        ...baseSelected,
        [name]: value,
      }
      const index = OrderedMedidas.indexOf(name)
      for (let i = index + 1; i < OrderedMedidas.length; i++) {
        nextSelected[OrderedMedidas[i]] = null
      }

      if (
        !hasAllMedidas &&
        !selectedMedidasArePartial(selectedMedidas) &&
        !selectedMedidasArePartial(nextSelected)
      ) {
        hydrateCatalogMedidas()
      }

      dispatch(
        updateMedidaSeleccionada({
          name,
          value,
        })
      )

      if (isHomeGuidedFlow) {
        const nextIndex = index + 1
        if (nextIndex < OrderedMedidas.length) {
          dispatch(fetchMedidaOpciones(OrderedMedidas[nextIndex]))
        }
        lastChangedMedidaRef.current = name
        lastAutoOpenedFor.current = null
        autoOpenPendingRef.current = null
      }

      if (hasCompleteMedidasSelection(nextSelected)) {
        setHomeEditInProgress(false)
        homeRestoreSnapshotRef.current = null
        lastChangedMedidaRef.current = null
        dispatch(setVehicleTyreSelectionWasPartial(false))
      }
    },
    [beginHomeMedidasEditIfNeeded, dispatch, hasAllMedidas, hydrateCatalogMedidas, isHomeGuidedFlow, legacyMedidas, medidasData, medidasForDisplay, selectedMedidas]
  )

  const nextMedidaToComplete = findNextMedidaToComplete(selectedMedidas)

  return {
    fields,
    hasAllEffectiveMedidas,
    hasCatalogMedidasContext,
    hasCompleteHomeMedidas,
    isFreshHomeSearch,
    isHomeGuidedFlow,
    isPartialVehicleMedidasSelection,
    isFieldDisabled,
    ensureFieldOptions,
    registerSelectRef,
    hydrateCatalogMedidas,
    handleMedidaChange,
    confirmPriorHomeMedidasForField,
    handleHomeMedidaBlur,
    abortHomeMedidasEdit,
    homeEditInProgress,
    nextMedidaToComplete,
  }
}

export function shouldPreserveMedidasOnLegacyReset(state: IAppState): boolean {
  // Preservem qualsevol mida ja triada (completa o parcial, p. ex. tornant
  // d'una landing de medida amb només ancho/serie/llanta) en navegar per la
  // web sense recàrrega. No cal isMedidasConfirmed: el preserve path el posa
  // a false i la següent visita a home no ha de tornar als defaults legacy.
  // Recàrrega per URL: selectedMedidas buit → false → reset amb defaults (correcte).
  return catalogHasAnyMedidaSelection(selectSelectedMedidas(state))
}

export function useShouldPreserveMedidasOnLegacyReset(): boolean {
  return useSelector(shouldPreserveMedidasOnLegacyReset)
}

export function buildLegacyMedidasLoadOptions(
  selectorState: SelectorState,
  fields: ReturnType<typeof useSyncedMedidasSelectorFields>['fields']
): Partial<SelectorState> {
  const opts: Partial<SelectorState> = { pristine: false }

  for (const name of OrderedMedidas) {
    const field = fields[name]
    opts[name] = {
      ...selectorState[name],
      value: field?.value ?? null,
      options: field?.options ?? [],
      loading: false,
    }
  }

  return opts
}

export function selectEffectiveEstacion(state: IAppState): Temporada {
  const selectorState = selectSelector(state)
  return resolveEffectiveEstacion(
    selectSelectedEstacion(state),
    selectorState.temporada?.value ?? null,
    isLegacyTemporadaLocked(state)
  )
}

/** Manté estació alineada entre selector legacy i Redux del catàleg. */
export function useSyncedEstacion(legacyDispatch: (action: unknown) => void) {
  const reduxDispatch = useDispatch()
  const legacyTemporada = useSelector(
    (state: IAppState) => selectSelector(state).temporada.value
  )
  const legacyTemporadaLocked = useSelector(isLegacyTemporadaLocked)
  const selectedEstacion = useSelector(selectSelectedEstacion)
  const estacionSeleccionadaPorUsuario = useSelector(
    selectEstacionSeleccionadaPorUsuario
  )

  useEffect(() => {
    if (!legacyTemporada) return
    if (selectedEstacion === legacyTemporada) return
    // No trepitjar una tria manual de temporada al catàleg (p.ex. després d'una
    // landing /neumaticos/:estacion/ amb temporada bloquejada).
    if (estacionSeleccionadaPorUsuario && selectedEstacion != null) return
    if (legacyTemporadaLocked || selectedEstacion == null) {
      dispatchCatalogEstacion(
        reduxDispatch,
        legacyTemporada,
        legacyTemporadaLocked
      )
    }
  }, [
    legacyTemporada,
    legacyTemporadaLocked,
    reduxDispatch,
    selectedEstacion,
    estacionSeleccionadaPorUsuario,
  ])

  useEffect(() => {
    if (!selectedEstacion) return
    if (legacyTemporadaLocked) return
    if (legacyTemporada === selectedEstacion) return
    legacyDispatch(selectEstacion(selectedEstacion))
  }, [
    legacyDispatch,
    legacyTemporada,
    legacyTemporadaLocked,
    selectedEstacion,
  ])

  const handleEstacionChange = useCallback(
    (value: string) => {
      const temporada = value as Temporada
      legacyDispatch(selectEstacion(temporada))
      reduxDispatch(setEstacionSeleccionada(temporada))
    },
    [legacyDispatch, reduxDispatch]
  )

  const effectiveEstacion = resolveEffectiveEstacion(
    selectedEstacion,
    legacyTemporada,
    legacyTemporadaLocked
  )

  const syncEstacionToRedux = useCallback(() => {
    dispatchCatalogEstacion(
      reduxDispatch,
      resolveEffectiveEstacion(
        selectedEstacion,
        legacyTemporada,
        legacyTemporadaLocked
      ),
      legacyTemporadaLocked
    )
  }, [
    legacyTemporada,
    legacyTemporadaLocked,
    reduxDispatch,
    selectedEstacion,
  ])

  return {
    effectiveEstacion,
    handleEstacionChange,
    syncEstacionToRedux,
  }
}
